import { ManagedSlot, ManagedBody } from "@/components/pages/ManagedPage";
import { useLanguage } from "@/i18n/LanguageContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO, {
  FAQJsonLd,
  BreadcrumbJsonLd,
  SoftwareApplicationJsonLd,
  SITE_URL,
} from "@/components/SEO";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useRef } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

export type FeatureItem = { title: string; desc: string };
export type Persona = { role: string; pain: string; value: string };
export type GainRow = {
  role: string;
  cares: string;
  gain: string;
  kpis: string;
  pitch: string;
};
export type FaqItem = { q: string; a: string };
export type UseCase = { persona: string; scenario: string; outcome: string };
export type Mockup = { src?: string; alt: string; caption?: string };
export type RelatedItem = { title: string; href: string; desc: string };
export type Step = { n: number | string; title: string; body: string };

export type Section =
  | { id?: string; kind: "prose"; eyebrow?: string; heading: string; body: string | ReactNode }
  | { id?: string; kind: "features"; eyebrow?: string; heading: string; intro?: string; items: FeatureItem[]; cols?: 2 | 3 }
  | { id?: string; kind: "checklist"; eyebrow?: string; heading: string; intro?: string; items: string[] }
  | { id?: string; kind: "kpi"; eyebrow?: string; heading: string; intro?: string; items: { value: string; label: string }[] }
  | { id?: string; kind: "split"; eyebrow?: string; heading: string; left: string[]; right: string[]; leftTitle: string; rightTitle: string }
  | { id?: string; kind: "personas"; eyebrow?: string; heading: string; intro?: string; items: Persona[] }
  | { id?: string; kind: "gains"; eyebrow?: string; heading: string; intro?: string; items: GainRow[] }
  | { id?: string; kind: "useCases"; eyebrow?: string; heading: string; intro?: string; items: UseCase[] }
  | { id?: string; kind: "steps"; eyebrow?: string; heading: string; intro?: string; items: Step[] }
  | { id?: string; kind: "chips"; eyebrow?: string; heading: string; intro?: string; items: string[] }
  | { id?: string; kind: "mockups"; eyebrow?: string; heading: string; intro?: string; items: Mockup[] }
  | { id?: string; kind: "faq"; eyebrow?: string; heading: string; intro?: string; items: FaqItem[] }
  | { id?: string; kind: "related"; eyebrow?: string; heading: string; intro?: string; items: RelatedItem[] }
  | { id?: string; kind: "callout"; eyebrow?: string; heading: string; body: string; href?: string; linkLabel?: string };

type Props = {
  eyebrow?: string;
  title: string;
  description: string;
  bullets?: string[];
  seoTitle?: string;
  seoDescription?: string;
  breadcrumb?: { name: string; to: string }[];
  sections?: Section[];
  schema?: "product" | null;
  primaryCta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  ctaHeading?: string;
  ctaBody?: string;
  ctaSecondary?: { label: string; to: string };
  heroVisual?: ReactNode;
};

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function LongPage({
  eyebrow,
  title,
  description,
  bullets,
  seoTitle,
  seoDescription,
  breadcrumb,
  sections = [],
  schema = null,
  primaryCta = { label: "Book a Demo", to: "/contact" },
  secondaryCta = { label: "See the platform", to: "/platform" },
  ctaHeading = "See BEEDIGIT in your factory context",
  ctaBody = "Book a 30-minute demo. We'll walk through your work centers, signals and ERP touchpoints.",
  ctaSecondary = { label: "INCIT-certified for SIRI", to: "/siri" },
  heroVisual,
}: Props) {
  const { t } = useLanguage();
  const location = useLocation();
  const pageUrl = `${SITE_URL}${location.pathname}`;

  const sectionsWithIds = sections.map((s) => ({
    ...s,
    id: s.id || slugify(s.heading),
  }));


  const faqs = sectionsWithIds
    .filter((s): s is Extract<Section, { kind: "faq" }> & { id: string } => s.kind === "faq")
    .flatMap((s) => s.items.map((i) => ({ question: i.q, answer: i.a })));

  return (
    <div className="min-h-screen">
      <SEO title={t(seoTitle || title)} description={t(seoDescription || description)} />
      {breadcrumb && (
        <BreadcrumbJsonLd
          items={[
            { name: "Home", url: SITE_URL },
            ...breadcrumb.map((b) => ({ name: b.name, url: `${SITE_URL}${b.to}` })),
            { name: title, url: pageUrl },
          ]}
        />
      )}
      {schema === "product" && (
        <SoftwareApplicationJsonLd name={title} description={description} url={pageUrl} />
      )}
      {faqs.length > 0 && <FAQJsonLd items={faqs} />}

      <Navigation />
      <ManagedSlot at="start" />
      <ManagedBody>
      <main className="pt-14">
        {/* Hero */}
        <section className="hero-wash border-b border-border">
          <div className="container mx-auto px-4 py-14 md:py-20 max-w-6xl">
            <div className={heroVisual ? "grid lg:grid-cols-2 gap-10 items-center" : ""}>
              <div>
            {breadcrumb && (
              <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
                <Link to="/" className="hover:text-foreground">{t("Home")}</Link>
                {breadcrumb.map((b) => (
                  <span key={b.to + b.name} className="inline-flex items-center gap-1">
                    <ChevronRight className="h-3 w-3" />
                    <Link to={b.to} className="hover:text-foreground">{t(b.name)}</Link>
                  </span>
                ))}
                <ChevronRight className="h-3 w-3" />
                <span className="text-foreground/70">{t(title)}</span>
              </nav>
            )}
            {eyebrow && <p className="eyebrow mb-3">{t(eyebrow)}</p>}
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 max-w-4xl">{t(title)}</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mb-6">{t(description)}</p>
            {bullets && bullets.length > 0 && (
              <ul className="grid sm:grid-cols-2 gap-2 max-w-3xl mb-8">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-foreground/85">
                    <Check className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                    {t(b)}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                {primaryCta.to.startsWith("#") ? (
                  <a href={primaryCta.to}>{t(primaryCta.label)}<ArrowRight className="ms-2 h-4 w-4" /></a>
                ) : (
                  <Link to={primaryCta.to}>{t(primaryCta.label)}<ArrowRight className="ms-2 h-4 w-4" /></Link>
                )}
              </Button>
              <Button variant="outline" asChild>
                {secondaryCta.to.startsWith("#") ? (
                  <a href={secondaryCta.to}>{t(secondaryCta.label)}</a>
                ) : (
                  <Link to={secondaryCta.to}>{t(secondaryCta.label)}</Link>
                )}
              </Button>
            </div>
              </div>
              {heroVisual && (
                <div className="w-full">{heroVisual}</div>
              )}
            </div>
          </div>
        </section>

        {/* Body */}
        <div className="container mx-auto px-4 max-w-6xl py-10 md:py-14">
          <div className="max-w-3xl mx-auto space-y-16 md:space-y-20">
            {sectionsWithIds.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                {"eyebrow" in s && s.eyebrow && <p className="eyebrow mb-3">{t(s.eyebrow)}</p>}
                <h2 className="text-2xl md:text-3xl font-bold mb-4">{t(s.heading)}</h2>
                <SectionRenderer section={s} />
              </section>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t(ctaHeading)}</h2>
            <p className="text-muted-foreground mb-6">{t(ctaBody)}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild size="lg">
                <Link to={primaryCta.to}>{t(primaryCta.label)}<ArrowRight className="ms-2 h-4 w-4" /></Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to={ctaSecondary.to}>{t(ctaSecondary.label)}</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      </ManagedBody>
      <ManagedSlot at="end" />
      <Footer />
    </div>
  );
}

function SectionRenderer({ section: s }: { section: Section & { id?: string } }) {
  const { t } = useLanguage();
  if (s.kind === "prose") {
    return typeof s.body === "string" ? (
      <p className="text-muted-foreground leading-relaxed max-w-3xl whitespace-pre-line">{t(s.body as string)}</p>
    ) : (
      <div className="text-muted-foreground leading-relaxed max-w-3xl">{s.body}</div>
    );
  }
  if (s.kind === "features") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-8">{t(s.intro)}</p>}
        <div className={`grid gap-4 ${s.cols === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {s.items.map((it) => (
            <div key={it.title} className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-semibold mb-2">{t(it.title)}</h3>
              <p className="text-sm text-muted-foreground">{t(it.desc)}</p>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "checklist") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-4xl">
          {s.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-sm text-foreground/85">
              <Check className="mt-0.5 h-4 w-4 text-primary shrink-0" />{t(it)}
            </li>
          ))}
        </ul>
      </>
    );
  }
  if (s.kind === "kpi") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {s.items.map((it) => (
            <div key={it.label} className="rounded-lg border border-border bg-card p-5">
              <div className="text-3xl font-bold text-primary mb-1">{t(it.value)}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t(it.label)}</div>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "split") {
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {[{ t: s.leftTitle, items: s.left }, { t: s.rightTitle, items: s.right }].map((c) => (
          <div key={c.t} className="rounded-lg border border-border bg-card p-6">
            <h3 className="font-semibold mb-3">{t(c.t)}</h3>
            <ul className="space-y-2">
              {c.items.map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm text-foreground/85">
                  <Check className="mt-0.5 h-4 w-4 text-primary shrink-0" />{t(x)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  if (s.kind === "personas") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.items.map((p) => (
            <div key={p.role} className="rounded-lg border border-border bg-card p-5">
              <div className="eyebrow mb-2">{t(p.role)}</div>
              <p className="text-sm text-muted-foreground mb-3"><span className="text-foreground/80">{t("Pain:")}</span> {t(p.pain)}</p>
              <p className="text-sm text-muted-foreground"><span className="text-foreground/80">{t("Gets:")}</span> {t(p.value)}</p>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "gains") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--section-alt))] text-foreground/80">
              <tr>
                <th className="text-left p-3 font-medium">{t("Role")}</th>
                <th className="text-left p-3 font-medium">{t("Cares about")}</th>
                <th className="text-left p-3 font-medium">{t("What they gain")}</th>
                <th className="text-left p-3 font-medium">{t("KPIs / outcomes")}</th>
                <th className="text-left p-3 font-medium">{t("Pitch")}</th>
              </tr>
            </thead>
            <tbody>
              {s.items.map((r) => (
                <tr key={r.role} className="border-t border-border align-top">
                  <td className="p-3 font-semibold text-foreground/90 whitespace-nowrap">{t(r.role)}</td>
                  <td className="p-3 text-muted-foreground">{t(r.cares)}</td>
                  <td className="p-3 text-muted-foreground">{t(r.gain)}</td>
                  <td className="p-3 text-muted-foreground">{t(r.kpis)}</td>
                  <td className="p-3 text-primary/90 italic">{t(r.pitch)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {s.items.map((r) => (
            <div key={r.role} className="rounded-lg border border-border bg-card p-4">
              <div className="font-semibold mb-2">{t(r.role)}</div>
              <p className="text-sm text-muted-foreground mb-1"><span className="text-foreground/70">{t("Cares:")}</span> {t(r.cares)}</p>
              <p className="text-sm text-muted-foreground mb-1"><span className="text-foreground/70">{t("Gain:")}</span> {t(r.gain)}</p>
              <p className="text-sm text-muted-foreground mb-1"><span className="text-foreground/70">{t("KPIs:")}</span> {t(r.kpis)}</p>
              <p className="text-sm text-primary/90 italic mt-2">{t(r.pitch)}</p>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "useCases") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.items.map((u) => (
            <div key={u.persona} className="rounded-lg border border-border bg-card p-5">
              <div className="eyebrow mb-2">{t(u.persona)}</div>
              <p className="text-sm text-foreground/85 mb-3">{t(u.scenario)}</p>
              <p className="text-sm text-primary/90"><Check className="inline h-4 w-4 me-1" />{t(u.outcome)}</p>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "steps") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <div className="grid md:grid-cols-3 gap-4">
          {s.items.map((st) => (
            <div key={st.title} className="rounded-lg border border-border bg-card p-5 relative">
              <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">{st.n}</div>
              <h3 className="font-semibold mb-2 mt-2">{t(st.title)}</h3>
              <p className="text-sm text-muted-foreground">{t(st.body)}</p>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "chips") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <div className="flex flex-wrap gap-2">
          {s.items.map((c) => (
            <span key={c} className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground/80">{t(c)}</span>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "mockups") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <MockupsCarousel items={s.items} />
      </>
    );
  }
  if (s.kind === "faq") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <Accordion type="single" collapsible className="max-w-3xl">
          {s.items.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left">{t(f.q)}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground whitespace-pre-line">{t(f.a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </>
    );
  }
  if (s.kind === "related") {
    return (
      <>
        {s.intro && <p className="text-muted-foreground max-w-3xl mb-6">{t(s.intro)}</p>}
        <div className="grid md:grid-cols-3 gap-4">
          {s.items.map((r) => (
            <Link
              key={r.href}
              to={r.href}
              className="rounded-lg border border-border bg-card p-5 hover:border-primary/60 hover:bg-card/80 transition-colors group"
            >
              <h3 className="font-semibold mb-2 group-hover:text-primary">{t(r.title)}</h3>
              <p className="text-sm text-muted-foreground mb-3">{t(r.desc)}</p>
              <span className="text-xs text-primary inline-flex items-center gap-1">{t("View module details")} <ArrowRight className="h-3 w-3" /></span>
            </Link>
          ))}
        </div>
      </>
    );
  }
  if (s.kind === "callout") {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-6">
        <p className="text-foreground/90 mb-3">{t(s.body)}</p>
        {s.href && s.linkLabel && (
          <Link to={s.href} className="text-primary text-sm inline-flex items-center gap-1 hover:underline">
            {t(s.linkLabel)} <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    );
  }
  return null;
}

function MockupsCarousel({ items }: { items: Mockup[] }) {
  const { t } = useLanguage();
  const autoplay = useRef(
    Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  return (
    <Carousel
      opts={{ loop: true, align: "start" }}
      plugins={[autoplay.current]}
      className="w-full"
    >
      <CarouselContent>
        {items.map((m, i) => (
          <CarouselItem key={i}>
            <figure className="rounded-lg border border-border bg-card overflow-hidden shadow-sm">
              {m.src ? (
                <img
                  src={m.src}
                  alt={m.alt}
                  loading="lazy"
                  className="w-full h-auto block aspect-[16/9] object-cover"
                />
              ) : (
                <div className="aspect-[16/9] bg-[hsl(var(--section-alt))] flex items-center justify-center text-xs text-muted-foreground">
                  {m.alt}
                </div>
              )}
              <figcaption className="p-5 border-t border-border">
                <div className="font-semibold mb-1">{m.alt}</div>
                {m.caption && (
                  <p className="text-sm text-muted-foreground">{t(m.caption)}</p>
                )}
              </figcaption>
            </figure>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="hidden sm:flex" />
      <CarouselNext className="hidden sm:flex" />
    </Carousel>
  );
}